/**
 * PATH: packages/email-hub/src/feature/lib/shellBackInterceptor.ts
 * FILE_ID: EMAILHUB-LIB-SHELL-BACK-INTERCEPTOR-001
 * PURPOSE: Tiny module-scope registry that lets a hub's inner page
 *          intercept the tenant shell's app-level "back" button
 *          (e.g. Newmark's `AppBackButton`) before the shell pops a
 *          route from its history stack.
 *
 * WHY:     BUG-2026-05-24-MOBILE-EMAIL-BACK-BUTTON-DEAD. The Newmark
 *          shell's `←` back button operates on the route history
 *          stack (records every `useLocation` change). On mobile,
 *          when the operator opens an email from the inbox list, the
 *          ROW CLICK doesn't change the URL — `InboxPage` just sets
 *          `selectedEmailId` in component state and renders the
 *          preview pane over the list. The shell never saw a route
 *          change, so pressing `←` either (a) does nothing because
 *          the stack only has one entry, or (b) navigates AWAY from
 *          `/email/inbox` to whatever came before it (e.g. Jobs).
 *          The operator perceived it as broken — there's no
 *          "back-to-list" affordance in the top nav, even though the
 *          `X` close button inside `EmailPreviewPane` does the right
 *          thing.
 *
 *          Rather than refactor the row-click into a route change
 *          (which would touch every hub and every tenant shell), we
 *          give the active page a way to TELL the shell: "I have
 *          local state; pop that first before popping the route".
 *          `InboxPage` registers an interceptor when
 *          `isNarrow && selectedEmail`; the shell consults the
 *          registry on every `←` click; if consumed, the route stack
 *          is untouched.
 *
 * FLOW:
 *   ┌──────────────────┐    ┌─────────────────────────┐    ┌──────────────────┐
 *   │  InboxPage       │ →  │  shellBackInterceptor   │ ←  │ AppBackButton    │
 *   │  (registers      │    │  (module-scope ref)     │    │  (consults       │
 *   │   when isNarrow  │    │                         │    │   before pop)    │
 *   │   && selected)   │    │                         │    │                  │
 *   └──────────────────┘    └─────────────────────────┘    └──────────────────┘
 *
 * Design notes:
 *   1. Module-scope (not React context) so non-React callers can
 *      consult it from the shell. The Newmark shell's
 *      `AppBackButton.onClick` is a plain DOM handler — we don't
 *      want to thread a context provider down from a tenant shell
 *      into a shared package.
 *   2. Single active interceptor — the assumption is the operator
 *      is interacting with ONE page at a time, and that page either
 *      owns local-back behaviour or it doesn't. If we ever need a
 *      stack (e.g. nested modal over inbox over kanban), we'll
 *      change `setShellBackInterceptor` to push and add a `pop`;
 *      until then, last-writer-wins is the right semantic — the
 *      most-recently-mounted page owns the interception.
 *   3. The interceptor returns `boolean` — `true` = "I consumed it,
 *      don't pop", `false` = "no thanks, pop normally". This lets a
 *      page register a CONDITIONAL handler (e.g. only consume when
 *      a sub-modal is open) without unregistering on every state
 *      flip.
 *   4. Pure I→O: `tryConsumeShellBack()` is a pure call against the
 *      stored ref; no React, no side effects beyond invoking the
 *      registered fn. Trivially unit-testable.
 *   5. NO global side effects on import. The ref is initialised to
 *      `null` and only filled when a page calls
 *      `setShellBackInterceptor(fn)`. SSR-safe: this module never
 *      touches `window` / `document`.
 */

/** The interceptor contract. Return `true` to indicate the back
 *  action was consumed locally and the shell should NOT navigate;
 *  return `false` to let the shell proceed with its normal pop. */
export type ShellBackInterceptor = () => boolean

/** Module-scope ref. Wrapped in an object so the value can be
 *  swapped without re-importing the module (which would create a
 *  new binding in ESM and lose the registration). */
const g_interceptorRef: { current: ShellBackInterceptor | null } = {
  current: null,
}

/** Subscribers notified when the registration flips from null →
 *  fn or fn → null. Used by the shell's back button to re-render
 *  its disabled state — the shell can't render `←` as enabled
 *  when `canGoBack` is false BUT an interceptor IS registered
 *  unless it knows when the registration changes.
 *
 *  Kept as a simple Set, not a Zustand store, so this module has
 *  zero external dependencies and stays trivially testable. */
const g_subscribers = new Set<() => void>()

function notifySubscribers(): void {
  for (const fn of g_subscribers) {
    try {
      fn()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[shellBackInterceptor] subscriber threw', err)
    }
  }
}

/** Register the active interceptor. Pass `null` to unregister.
 *  Returns a cleanup function so React effects can do
 *  `useEffect(() => setShellBackInterceptor(fn), [...])` if they
 *  prefer the cleanup-return pattern.
 *
 *  Last-writer-wins: the most recent caller owns interception
 *  until they explicitly clear or another caller replaces them.
 *  This matches the "operator interacts with one page at a time"
 *  assumption above. */
export function setShellBackInterceptor(
  fn: ShellBackInterceptor | null,
): () => void {
  const prev = g_interceptorRef.current
  g_interceptorRef.current = fn
  // Only notify when registration PRESENCE changed (null↔fn), not on
  // fn-swap-with-fn — the shell only re-renders for "is anything
  // registered" decisions, swapping one fn for another doesn't
  // change that bit and would churn re-renders for no benefit.
  if ((prev === null) !== (fn === null)) {
    notifySubscribers()
  }
  return () => {
    // Only clear if WE are still the active interceptor — protects
    // against an out-of-order cleanup overwriting a newer
    // registration from a different mount that happened in between.
    if (g_interceptorRef.current === fn) {
      g_interceptorRef.current = null
      notifySubscribers()
    }
  }
}

/** Subscribe to registration-presence changes. Subscribers fire
 *  whenever the active interceptor flips between `null` and
 *  `fn` (in either direction). Returns an unsubscribe cleanup.
 *
 *  Use case: a tenant shell's `←` button needs to enable itself
 *  whenever an in-hub page registers an interceptor (so the
 *  operator can actually trigger it), even when the route-history
 *  stack is shallow enough that `canGoBack` would otherwise
 *  disable the button. Subscribe with a `forceUpdate` callback. */
export function subscribeShellBackInterceptor(fn: () => void): () => void {
  g_subscribers.add(fn)
  return () => {
    g_subscribers.delete(fn)
  }
}

/** Cheap selector — true iff an interceptor is currently
 *  registered. Used by the shell's back button to decide whether
 *  to render itself as enabled even when `canGoBack` is false. */
export function hasShellBackInterceptor(): boolean {
  return g_interceptorRef.current !== null
}

/** Shell-side helper. Returns `true` if a registered interceptor
 *  consumed the back action — in which case the caller MUST NOT
 *  proceed with its own pop / navigate. Returns `false` when no
 *  interceptor is registered OR the registered one declined the
 *  consume.
 *
 *  Defensive against an interceptor that throws — if the
 *  registered fn throws, we log to console.warn (so a future
 *  agent debugging a "back button stopped working" report can
 *  grep the symptom) and return `false` so the shell falls back
 *  to its normal pop. Never trap the operator on a thrown
 *  interceptor. */
export function tryConsumeShellBack(): boolean {
  const fn = g_interceptorRef.current
  if (!fn) return false
  try {
    return fn() === true
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[shellBackInterceptor] registered interceptor threw - falling back to normal pop',
      err,
    )
    return false
  }
}

/** Test-only escape hatch. Clears the module-scope ref AND every
 *  subscriber so each test can assert its own register/consume
 *  behaviour without cross-contamination. Not part of the public
 *  surface. */
export function __resetShellBackInterceptorForTests(): void {
  g_interceptorRef.current = null
  g_subscribers.clear()
}
